/**
 * Draggable popup helper, ported from ``Module/index.tsx`` (~line 299).
 *
 * Attaches mouse + touch handlers to ``handle`` so that ``element`` can
 * be moved within the bounds of ``parent``. Mirrors the legacy behavior
 * 1:1: position is clamped inside the parent rect (with optional
 * padding) and the initial transform is cleared on the first drag so
 * the element starts at its natural offset.
 *
 * Returned function unbinds the handlers — call it on unmount.
 */
export function attachDragMove(
  element: HTMLElement,
  handle: HTMLElement,
  parent: HTMLElement,
  padding = 0,
): () => void {
  let pos1 = 0;
  let pos2 = 0;
  let pos3 = 0;
  let pos4 = 0;

  // Pointer events cover mouse + touch (iPad) in one path. The handle sets
  // touch-action:none (DigitalSHSAT/index.css) so touch-dragging doesn't scroll
  // the page — unlike the old touchmove listener, which was passive-by-default
  // on document and so couldn't preventDefault the scroll.
  const dragMouseDown = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    element.style.transform = 'none';
    pos3 = event.clientX;
    pos4 = event.clientY;
    document.addEventListener('pointermove', dragElement);
    document.addEventListener('pointerup', closeDragElement);
  };

  const dragElement = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    pos1 = pos3 - event.clientX;
    pos2 = pos4 - event.clientY;
    pos3 = event.clientX;
    pos4 = event.clientY;

    const parentRect = parent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    let newTop = element.offsetTop - pos2;
    let newLeft = element.offsetLeft - pos1;
    newTop = Math.max(
      padding,
      Math.min(newTop, parentRect.height - elementRect.height - padding),
    );
    newLeft = Math.max(
      padding,
      Math.min(newLeft, parentRect.width - elementRect.width - padding),
    );

    element.style.top = `${newTop}px`;
    element.style.left = `${newLeft}px`;
  };

  const closeDragElement = () => {
    document.removeEventListener('pointermove', dragElement);
    document.removeEventListener('pointerup', closeDragElement);
  };

  handle.addEventListener('pointerdown', dragMouseDown);

  return () => {
    handle.removeEventListener('pointerdown', dragMouseDown);
    closeDragElement();
  };
}
